import { type JSX, type Ref, useImperativeHandle, useState } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useIslandHost } from "@frontend/contexts";

import { ClientEditor } from "./ClientEditor";
import {
  type ClientEditorDataProps,
  clientEditorDataSchema,
} from "./ClientEditor.schema";
import { ClientEditorDialog } from "./ClientEditorDialog";

export type ClientEditorHandle = {
  open: (clientId?: number | null) => void;
  hide: () => void;
};

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    "client-editor": {
      props: ClientEditorDataProps;
      handle: ClientEditorHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-client-editor": HTMLElement & ClientEditorHandle;
  }
}

export function RWCClientEditor({
  ref,
  clientId: initialClientId,
  contained,
  open: initialOpen,
}: ClientEditorDataProps & { ref?: Ref<ClientEditorHandle> }): JSX.Element {
  const host = useIslandHost();

  const [clientId, setClientId] = useState<number | null>(initialClientId);

  const [open, setOpen] = useState(initialOpen);

  useImperativeHandle(
    ref,
    () => ({
      open: (next = null) => {
        setClientId(next ?? null);
        setOpen(true);
      },
      hide: () => setOpen(false),
    }),
    [],
  );

  function emit(eventType: string, detail: Record<string, unknown> = {}) {
    host?.dispatchEvent(new CustomEvent(eventType, { detail, bubbles: true }));
  }

  const onSaved = (savedId: number) =>
    emit("client:saved", { clientId: savedId });

  const onCancel = () => emit("client:cancel");

  if (contained) {
    return (
      <ClientEditor clientId={clientId} onSaved={onSaved} onCancel={onCancel} />
    );
  }

  return (
    <ClientEditorDialog
      open={open}
      onOpenChange={setOpen}
      clientId={clientId}
      onSaved={onSaved}
      onCancel={onCancel}
    />
  );
}

componentManager.register(
  rwc("client-editor", {
    component: RWCClientEditor,
    dataSchema: clientEditorDataSchema,
  }),
);
